import { AttachmentCategory, AttachmentType } from '@@types/api.types';
import { errorToast } from '@hooks/toast.hook';
import {
  type BufferedAttachment,
  useInnovatorCreateAttachmentUrl,
  useInnovatorUploadAttachmentFile,
} from '@queries/attachments.query';
import { queryKeys } from '@queries/keys';
import { type SurveyAttachmentsStore, useSurveyAttachmentsStore } from '@stores/survey-attachments.store';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

const FAILED_TITLES_PREVIEW_LIMIT = 3;

type CreateAttachmentUrlMutation = ReturnType<typeof useInnovatorCreateAttachmentUrl>['mutateAsync'];
type UploadAttachmentFileMutation = ReturnType<typeof useInnovatorUploadAttachmentFile>['mutateAsync'];

type UploadResult = {
  bufferedAttachment: BufferedAttachment;
  status: 'failed' | 'success';
};

type UseSyncBufferedAnswerAttachmentsParams = {
  answerId?: string;
  bufferedAttachments: BufferedAttachment[];
  canEdit: boolean;
  questionAttachmentKey?: string;
};

type ClearBufferedAttachments = SurveyAttachmentsStore['clearBufferedAttachments'];
type UpdateBufferedAttachments = SurveyAttachmentsStore['updateBufferedAttachments'];

const buildFailedUploadsDescription = (params: {
  failedCount: number;
  failedTitles: string[];
  successfulCount: number;
}): string => {
  const { failedCount, failedTitles, successfulCount } = params;

  if (failedTitles.length === 0) {
    return successfulCount > 0
      ? `Saved ${successfulCount} attachments. Failed ${failedCount} attachments.`
      : `Failed to save ${failedCount} attachments.`;
  }

  const failedTitlesPreview = failedTitles
    .slice(0, FAILED_TITLES_PREVIEW_LIMIT)
    .map((title) => `"${title}"`)
    .join(', ');
  const hasMoreTitles = failedTitles.length > FAILED_TITLES_PREVIEW_LIMIT ? ', ...' : '';

  return successfulCount > 0
    ? `Saved ${successfulCount} attachments. Failed ${failedCount}: ${failedTitlesPreview}${hasMoreTitles}.`
    : `Failed to save ${failedCount} attachments: ${failedTitlesPreview}${hasMoreTitles}.`;
};

const syncSingleBufferedAttachment = async (params: {
  answerId: string;
  bufferedAttachment: BufferedAttachment;
  createAttachmentUrl: CreateAttachmentUrlMutation;
  uploadAttachmentFile: UploadAttachmentFileMutation;
}): Promise<UploadResult> => {
  const { answerId, bufferedAttachment, createAttachmentUrl, uploadAttachmentFile } = params;

  try {
    if (bufferedAttachment.type === AttachmentType.URL) {
      await createAttachmentUrl({
        category: bufferedAttachment.category,
        description: bufferedAttachment.description,
        targetId: answerId,
        targetType: 'answer',
        title: bufferedAttachment.title,
        url: bufferedAttachment.url ?? '',
      });

      return { bufferedAttachment, status: 'success' };
    }

    if (!bufferedAttachment.file) {
      return { bufferedAttachment, status: 'success' };
    }

    await uploadAttachmentFile({
      category: bufferedAttachment.category,
      description: bufferedAttachment.description,
      file: bufferedAttachment.file,
      targetId: answerId,
      targetType: 'answer',
      title: bufferedAttachment.title,
    });

    return { bufferedAttachment, status: 'success' };
  } catch {
    return { bufferedAttachment, status: 'failed' };
  }
};

const keepOnlyFailedBufferedAttachments = (params: {
  bufferedAttachments: BufferedAttachment[];
  failedUploads: UploadResult[];
  questionAttachmentKey: string;
  updateBufferedAttachments: UpdateBufferedAttachments;
}): void => {
  const { bufferedAttachments, failedUploads, questionAttachmentKey, updateBufferedAttachments } = params;
  const failedClientIds = new Set(failedUploads.map((uploadResult) => uploadResult.bufferedAttachment.clientId));
  const failedAttachments = bufferedAttachments.filter((attachment) => failedClientIds.has(attachment.clientId));

  updateBufferedAttachments({
    attachments: failedAttachments,
    questionAttachmentKey,
  });
};

const getTrimmedFailedTitles = (failedUploads: UploadResult[]): string[] => {
  return failedUploads
    .map((uploadResult) => uploadResult.bufferedAttachment.title.trim())
    .filter((title) => title.length > 0);
};

const refetchAnswerAttachments = async (params: {
  answerId: string;
  queryClient: ReturnType<typeof useQueryClient>;
}): Promise<void> => {
  const { answerId, queryClient } = params;

  await queryClient.refetchQueries({
    queryKey: queryKeys.attachments.getAnswerAttachments({
      answerId,
      category: AttachmentCategory.GENERAL,
    }),
  });
};

const useClearBufferedAttachments = (): ClearBufferedAttachments => {
  return useSurveyAttachmentsStore((state) => state.clearBufferedAttachments);
};

const useUpdateBufferedAttachments = (): UpdateBufferedAttachments => {
  return useSurveyAttachmentsStore((state) => state.updateBufferedAttachments);
};

export const useSyncBufferedAnswerAttachments = (params: UseSyncBufferedAnswerAttachmentsParams) => {
  const { answerId, bufferedAttachments, canEdit, questionAttachmentKey } = params;
  const clearBufferedAttachments = useClearBufferedAttachments();
  const updateBufferedAttachments = useUpdateBufferedAttachments();
  const queryClient = useQueryClient();
  const { mutateAsync: createAttachmentUrl } = useInnovatorCreateAttachmentUrl();
  const { mutateAsync: uploadAttachmentFile } = useInnovatorUploadAttachmentFile();
  const [isSyncingBufferedAttachments, setIsSyncingBufferedAttachments] = useState(false);
  const syncInProgressRef = useRef(false);

  useEffect(() => {
    if (!answerId || bufferedAttachments.length === 0 || !canEdit || !questionAttachmentKey) return;
    if (syncInProgressRef.current) return;

    let isCancelled = false;
    syncInProgressRef.current = true;

    const syncBufferedAttachments = async (): Promise<void> => {
      setIsSyncingBufferedAttachments(true);

      try {
        const uploadResults = await Promise.all(
          bufferedAttachments.map((bufferedAttachment) =>
            syncSingleBufferedAttachment({
              answerId,
              bufferedAttachment,
              createAttachmentUrl,
              uploadAttachmentFile,
            }),
          ),
        );

        if (isCancelled) return;

        const failedUploads = uploadResults.filter((result) => result.status === 'failed');
        const successfulUploads = uploadResults.filter((result) => result.status === 'success');

        if (successfulUploads.length > 0) {
          await refetchAnswerAttachments({
            answerId,
            queryClient,
          });
        }

        if (isCancelled) return;

        if (failedUploads.length === 0) {
          clearBufferedAttachments(questionAttachmentKey);
          return;
        }

        keepOnlyFailedBufferedAttachments({
          bufferedAttachments,
          failedUploads,
          questionAttachmentKey,
          updateBufferedAttachments,
        });

        const failedTitles = getTrimmedFailedTitles(failedUploads);
        errorToast({
          description: buildFailedUploadsDescription({
            failedCount: failedUploads.length,
            failedTitles,
            successfulCount: successfulUploads.length,
          }),
        });
      } catch {
        if (!isCancelled) {
          errorToast({ description: 'Failed to save buffered attachments.' });
        }
      } finally {
        if (!isCancelled) {
          setIsSyncingBufferedAttachments(false);
        }
        syncInProgressRef.current = false;
      }
    };

    void syncBufferedAttachments();

    return () => {
      isCancelled = true;
    };
  }, [
    answerId,
    bufferedAttachments,
    canEdit,
    clearBufferedAttachments,
    createAttachmentUrl,
    questionAttachmentKey,
    queryClient,
    updateBufferedAttachments,
    uploadAttachmentFile,
  ]);

  return { isSyncingBufferedAttachments };
};
